import { isString, get, omitBy, isNil } from 'lodash-es';
import moment from 'moment';
import type { CoreTypes } from '../../core';
import type { Group } from '../types/groups';
import { getDivision, getDivisionId, hasDivisionId, toReference, type DivisionScope } from './common';
import toPerson from './persons';

type Expand = ('referenceNames' | 'persons')[];

/**
 * @deprecated This is only used for SCS. can be removed when scs use v2
 */
export default (
  groups:     Extract<CoreTypes.Mixed.Group, { id: string }>[],
  domain?:    DivisionScope,
  expand?:    Expand
): Group[] => {
  if (!Array.isArray(groups))
    throw new Error('(SS12000::To::Groups) Group input is not of array type', { cause: 'invalid_input' });

  if (!groups.every((group) => isString(group) || isString(get(group, 'id'))))
    throw new Error('(SS12000::To::Groups) Some groups are missing "id" values', { cause: 'invalid_input' });

  if (!domain && !groups.every((group) => !isString(group) && hasDivisionId(group)))
    throw new Error('(SS12000::To::Groups) Some top layer groups are missing division values', { cause: 'invalid_input' });

  return groups.map((group) => {
    const members = !isString(group) && Array.isArray(group.members)
      ? (group.members).map((member) => {
        if (isString(member)) return { id: member } as Extract<CoreTypes.Mixed.Person, { id: string }>;
        return member as Extract<CoreTypes.Mixed.Person, { id: string }>;
      })
      : [];

    const division = getDivision(group, domain);
    if (!division || isString(division) || !('id' in division && division.id))
      throw new Error('(SS12000::To::Groups) Some group members are missing division values', { cause: 'invalid_input' });
    if (!('start' in division && division.start) || !('end' in division && division.end))
      throw new Error('(SS12000::To::Groups) Some group members are missing division start or end values', { cause: 'invalid_input' });

    return {
      id:        `${ getDivisionId(group, domain) }.${ isString(group) ? group : group.id }`,
      groupType: group.species === 'class' ? 'Klass' : 'Undervisning',
      startDate: moment(division.start).toISOString(),
      endDate:   moment(division.end).toISOString(),
      ...!isString(group) && {
        meta: {
          created:  moment(group.createdAt).toISOString(),
          modified: moment(group.updatedAt).toISOString()
        },
        /*
          Optionally add the following properties
        */
        ...omitBy({
          foreignId:   group.ids,
          displayName: group.displayName,
        }, isNil),
        ...group.members && {
          // @ts-ignore
          groupMemberships: toReference(members, division).map(person => ({
            startDate: moment(division.start).toISOString(),
            endDate:   moment(division.end).toISOString(),
            person:    person
          }))
        },
        ...expand?.some(x => ['persons'].includes(x)) && {
          _embedded: {
            ...expand.includes('persons') && {
              groups: toPerson(members, division as Extract<CoreTypes.Deep.Division, { id: string }>)
            }
          }
        }
      }
    };
  });
};